import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../colors";
import DarkHeroPageEffects from "../DarkHeroPageEffects";
import { PageContainer } from "../PageContainer";

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const HERO_BG = "#080808";

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "80px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const cardSx = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "18px",
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const descSx = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
  mb: { xs: 4, md: 5 },
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.contrast,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  border: "1px solid transparent",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease, border-color .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.12),
        transparent
      )
    `,
    transform: "translateX(-120%)",
    transition: "transform .6s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow: "0 10px 30px rgba(232,163,61,0.18)",
  },
  "&:hover::before": {
    transform: "translateX(120%)",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background:
      "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background:
      "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": {
    opacity: 1,
  },
  "&:active": {
    transform: "translateY(0px)",
  },
};

const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";

const PLAN_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(3, minmax(0, 1fr))",
  },
  gap: "1.5rem",
  mt: { xs: "2.5rem", md: "3rem" },
  maxWidth: "68rem",
  mx: "auto",
};

const BILLING_SELECTOR_SX = {
  mt: { xs: "2.5rem", md: "3rem" },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const BILLING_SELECTOR_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: { xs: 1, sm: 1.25, md: 1.5 },
};

const BILLING_CONTROL_SX = {
  position: "relative",
  display: "flex",
  padding: "4px",
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  minWidth: 260,
  height: 48,
  boxSizing: "border-box",
};

const BILLING_LABEL_SX = {
  position: "relative",
  zIndex: 1,
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.74rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  border: 0,
  background: "transparent",
  cursor: "pointer",
  userSelect: "none",
  px: 1.5,
  py: 0,
};

const SAVE_PROMO_BADGE_SX = {
  height: 48,
  px: 1.4,
  display: "flex",
  alignItems: "center",
  border: "1px solid rgba(232,163,61,0.18)",
  background: "rgba(232,163,61,0.04)",
  color: colors.primary.main,
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

const PLAN_BADGE_SX = {
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  px: 1,
  py: 0.35,
  border: `0.5px solid ${colors.primary.main}`,
  fontSize: "0.62rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: colors.primary.main,
};

const PLAN_CARD_SX = {
  position: "relative",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.5,
  width: "100%",
  minWidth: 0,
  height: { md: "100%" },
  p: { xs: "2rem 1.5rem", md: "2.5rem 2rem" },
  border: `0.5px solid ${colors.border.default}`,
  backgroundColor: "transparent",
  transition: "border-color 0.25s, transform 0.25s",
  "@media (hover: hover)": {
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
};

const PLAN_CARD_FEATURED_SX = {
  borderColor: "rgba(200, 135, 42, 0.3)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(to right, transparent, #C8872A, transparent)",
    pointerEvents: "none",
  },
};

const FEATURE_LIST_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.55,
  width: "100%",
};

const FEATURE_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.6,
  width: "100%",
  fontSize: "0.8rem",
  lineHeight: 1.45,
  color: colors.text.primary,
  "&::before": {
    content: '""',
    display: "block",
    width: 4,
    height: 4,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: colors.primary.main,
  },
};

const PLAN_DIVIDER_SX = {
  width: "100%",
  height: "0.5px",
  backgroundColor: colors.border.default,
  flexShrink: 0,
};

const PLANS = [
  {
    key: "free",
    tier: "Tier 01",
    name: "Free",
    monthlyPrice: "€0",
    annualMonthlyPrice: "€0",
    annualCompareAt: null,
    annualTotal: null,
    annualBadge: null,
    featured: false,
    features: [
      { text: "Unlimited matchmaking access", active: true },
      { text: "Default matchmaking settings", active: true },
      { text: "All maps and locations active", active: true },
      { text: "Standard queue placement", active: true },
    ],
  },
  {
    key: "premium",
    tier: "Tier 02",
    name: "Premium",
    monthlyPrice: "€9.15",
    annualMonthlyPrice: "€6.99",
    annualCompareAt: "€9.15",
    annualTotal: "€83.88",
    annualBadge: "Best value",
    featured: true,
    features: [
      { text: "Full matchmaking settings access", active: true },
      { text: "Premium tier servers", active: true },
      { text: "Refine down to 3 active maps", active: true },
      { text: "Refine down to 3 active server locations", active: true },
      { text: "Expanded queue control", active: true },
      { text: "Premium profile badge", active: true },
    ],
  },
  {
    key: "pro",
    tier: "Tier 03",
    name: "Pro",
    monthlyPrice: "€16.99",
    annualMonthlyPrice: "€12.99",
    annualCompareAt: "€16.99",
    annualTotal: "€155.88",
    annualBadge: "Save 24%",
    featured: false,
    features: [
      { text: "Everything in Premium", active: true },
      { text: "Premium tier servers", active: true },
      { text: "Captain priority in team", active: true },
      { text: "ELO recalibration requests", active: true },
      { text: "Can refine down to 1 active map", active: true },
      { text: "Can refine down to 1 active server location", active: true },
      { text: "Priority queue placement", active: true },
      { text: "Pro profile badge", active: true },
    ],
  },
];

const COMPARE_SECTIONS = [
  {
    label: "Queue controls",
    rows: [
      { feature: "Advanced matchmaking settings", free: false, premium: true, pro: true },
      { feature: "Map pool refinement", free: "All maps", premium: "Min. 3 maps", pro: "Down to 1 map", proHighlight: true },
      { feature: "Server location refinement", free: "All locations", premium: "Min. 3 locations", pro: "Down to 1 location", proHighlight: true },
      { feature: "Premium tier servers", free: false, premium: true, pro: true },
      { feature: "Queue placement", free: "Standard", premium: "Standard", pro: "Priority", proGold: true },
    ],
  },
  {
    label: "Competitive extras",
    rows: [
      { feature: "Reputation profile", free: true, premium: true, pro: true },
      { feature: "Impact Rating", free: true, premium: true, pro: true },
      { feature: "Captain priority", free: false, premium: false, pro: true },
      { feature: "ELO recalibration requests", free: false, premium: false, pro: true },
      { feature: "Pro profile badge", free: false, premium: false, pro: true },
    ],
  },
];

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionIntro({ label, title, description }) {
  return (
    <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
      <Eyebrow>{label}</Eyebrow>
      <Typography component="h2" sx={titleSx}>{title}</Typography>
      {description ? <Typography sx={descSx}>{description}</Typography> : null}
    </MotionBox>
  );
}

function PlanFeatureList({ features }) {
  return (
    <Box sx={FEATURE_LIST_SX}>
      {features.filter((f) => f.active).map((feature) => (
        <Typography key={feature.text} component="div" sx={FEATURE_SX}>
          {feature.text}
        </Typography>
      ))}
    </Box>
  );
}

function BillingCycleToggle({ isAnnual, onChange }) {
  return (
    <Box sx={BILLING_SELECTOR_SX}>
      <Box
        sx={{
          width: 48,
          height: "1px",
          background: colors.primary.main,
          mx: "auto",
          mb: 1.5,
          opacity: 0.6,
        }}
      />
      <Box sx={BILLING_SELECTOR_ROW_SX}>
        <Box sx={BILLING_CONTROL_SX}>
          <MotionBox
            aria-hidden
            animate={{ x: isAnnual ? "100%" : "0%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              width: "calc(50% - 4px)",
              height: "calc(100% - 8px)",
              background:
                "linear-gradient(180deg, rgba(232,163,61,0.16), rgba(232,163,61,0.08))",
              border: "1px solid rgba(232,163,61,0.2)",
              pointerEvents: "none",
            }}
          />
          <MotionTypography
            component="button"
            type="button"
            onClick={() => onChange(false)}
            animate={{ color: !isAnnual ? colors.text.primary : colors.text.muted }}
            transition={{ duration: 0.25 }}
            sx={BILLING_LABEL_SX}
          >
            Monthly
          </MotionTypography>
          <MotionTypography
            component="button"
            type="button"
            onClick={() => onChange(true)}
            animate={{ color: isAnnual ? colors.text.primary : colors.text.muted }}
            transition={{ duration: 0.25 }}
            sx={BILLING_LABEL_SX}
          >
            Annual
          </MotionTypography>
        </Box>
        <Box sx={SAVE_PROMO_BADGE_SX}>Save up to 24%</Box>
      </Box>
      <Typography sx={{ mt: 1.5, fontSize: "0.8rem", color: colors.text.muted, textAlign: "center" }}>
        Annual billing saves up to 24% compared to monthly.
      </Typography>
    </Box>
  );
}

function PlanPricing({ plan, isAnnual }) {
  const displayPrice = isAnnual ? plan.annualMonthlyPrice : plan.monthlyPrice;
  const showAnnualDetails = isAnnual && plan.annualCompareAt && plan.annualTotal;

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {isAnnual && plan.annualBadge ? (
        <Box sx={{ ...PLAN_BADGE_SX, mb: 1.25 }}>{plan.annualBadge}</Box>
      ) : null}
      <Typography
        component="div"
        sx={{
          fontFamily: "ui-monospace, monospace",
          fontSize: "0.9rem",
          color: colors.text.secondary,
        }}
      >
        <Box
          component="strong"
          sx={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: "2rem",
            fontWeight: 700,
            letterSpacing: "-0.04em",
            lineHeight: 1,
            color: colors.text.primary,
            mr: 0.5,
          }}
        >
          {displayPrice}
        </Box>
        / month
      </Typography>
      {showAnnualDetails ? (
        <>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: "0.85rem",
              color: colors.text.muted,
              textDecoration: "line-through",
            }}
          >
            {plan.annualCompareAt} / month
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: "0.85rem", color: colors.text.muted }}>
            Billed{" "}
            <Box component="span" sx={{ fontWeight: 700, color: colors.primary.main }}>
              {plan.annualTotal}
            </Box>{" "}
            / year
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

function PlanCard({ plan, index, isAnnual }) {
  const featured = plan.featured;

  return (
    <MotionBox
      initial={REVEAL.initial}
      whileInView={REVEAL.whileInView}
      viewport={REVEAL.viewport}
      transition={{ duration: 0.9, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      sx={{ width: "100%", minWidth: 0 }}
    >
      <Box sx={[PLAN_CARD_SX, featured && PLAN_CARD_FEATURED_SX]}>
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 600,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: colors.text.muted,
          }}
        >
          {plan.tier}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: "2rem",
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: featured ? colors.primary.main : colors.text.primary,
            ...(featured && {
              background: GOLD_GRADIENT,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }),
          }}
        >
          {plan.name}
        </Typography>
        <PlanPricing plan={plan} isAnnual={isAnnual} />
        <Box sx={PLAN_DIVIDER_SX} />
        <Box sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <PlanFeatureList features={plan.features} />
        </Box>
      </Box>
    </MotionBox>
  );
}

function CompareIcon({ yes }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        opacity: yes ? 1 : 0.3,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 14 14"
        fill="none"
        strokeWidth="2"
        sx={{ width: 14, height: 14, stroke: yes ? colors.primary.main : colors.text.muted }}
      >
        {yes ? (
          <polyline points="2.5,7 6,10.5 11.5,3.5" />
        ) : (
          <>
            <line x1="3.5" y1="3.5" x2="10.5" y2="10.5" />
            <line x1="10.5" y1="3.5" x2="3.5" y2="10.5" />
          </>
        )}
      </Box>
    </Box>
  );
}

function CompareCell({ value, highlight, gold, muted }) {
  if (typeof value === "boolean") {
    return <CompareIcon yes={value} />;
  }

  return (
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: gold ? colors.primary.main : muted ? colors.text.muted : colors.text.secondary,
        fontWeight: highlight || gold ? 600 : 400,
      }}
    >
      {value}
    </Typography>
  );
}

export default function PricingPage() {
  const [isAnnual, setIsAnnual] = React.useState(true);

  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <DarkHeroPageEffects watermark="PRICING" />

        <Box sx={{ position: "relative", zIndex: 2, maxWidth: "75rem", mx: "auto", width: "100%" }}>
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Eyebrow>Pricing</Eyebrow>
          </MotionBox>

          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 860,
              mb: 4,
            }}
          >
            Join free.
            <br />
            Upgrade when you
            <br />
            want more{" "}
            <Box component="span" sx={{ background: GOLD_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              control.
            </Box>
          </MotionBox>

          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "32rem",
                mb: 6,
              }}
            >
              Free access gets you into the founding community. Premium and Pro unlock deeper control over maps, servers, and queue behavior — whenever you&apos;re ready for it.
            </Typography>
          </MotionBox>

          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join for free
            </Box>
            <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
              How it works →
            </Box>
          </MotionBox>
        </Box>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Membership"
            title="Choose your level."
            description="All plans include full matchmaking access. Paid tiers add precision — finer map control, better servers, and queue advantages."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <BillingCycleToggle isAnnual={isAnnual} onChange={setIsAnnual} />
            <Box sx={PLAN_GRID_SX}>
              {PLANS.map((plan, index) => (
                <PlanCard key={plan.key} plan={plan} index={index} isAnnual={isAnnual} />
              ))}
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Compare plans"
            title="Everything, side by side."
            description="Every feature across every tier. No asterisks."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{ overflowX: "auto", mt: 8 }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 640,
                borderCollapse: "collapse",
                fontSize: "0.8125rem",
              }}
            >
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: `1px solid ${colors.border.focus}` }}>
                  {["Feature", "Free", "Premium", "Pro"].map((heading, i) => (
                    <Box
                      component="th"
                      key={heading}
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        textAlign: i === 0 ? "left" : "center",
                        pb: 2.5,
                        color: i === 2 ? colors.primary.main : colors.text.muted,
                        width: i === 0 ? "42%" : "19%",
                      }}
                    >
                      {heading}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {COMPARE_SECTIONS.map((section) => (
                  <React.Fragment key={section.label}>
                    <Box component="tr">
                      <Box
                        component="td"
                        colSpan={4}
                        sx={{
                          pt: 3,
                          pb: 1,
                          fontSize: "0.5625rem",
                          fontWeight: 700,
                          letterSpacing: "0.2em",
                          textTransform: "uppercase",
                          color: colors.text.muted,
                        }}
                      >
                        {section.label}
                      </Box>
                    </Box>
                    {section.rows.map((row) => (
                      <Box
                        component="tr"
                        key={row.feature}
                        sx={{
                          borderBottom: `1px solid ${colors.border.subtle}`,
                          "&:hover": { background: "rgba(255,255,255,0.015)" },
                        }}
                      >
                        <Box component="td" sx={{ py: "14px", color: colors.text.secondary, verticalAlign: "middle" }}>
                          {row.feature}
                        </Box>
                        {["free", "premium", "pro"].map((tier) => (
                          <Box
                            key={tier}
                            component="td"
                            sx={{ py: "14px", textAlign: "center", verticalAlign: "middle" }}
                          >
                            <CompareCell
                              value={row[tier]}
                              highlight={tier === "pro" && row.proHighlight}
                              gold={tier === "pro" && row.proGold}
                              muted={tier === "free" && typeof row.free === "string"}
                            />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={{ ...sectionSx, pt: 0 }}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <MotionBox
            {...REVEAL}
            transition={REVEAL_TRANSITION}
            sx={{
              ...cardSx,
              borderColor: colors.border.focus,
              background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
              p: { xs: "36px 28px", md: "48px 56px" },
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
              gap: { xs: 3, md: 5 },
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "35%",
                height: "1px",
                pointerEvents: "none",
                background: `linear-gradient(90deg, transparent, ${colors.primary.main}, transparent)`,
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: { xs: "2rem", md: "3rem" },
                fontWeight: 700,
                color: "rgba(232,163,61,0.15)",
                lineHeight: 1,
              }}
            >
              01
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: colors.text.primary,
                  mb: 1,
                }}
              >
                Refer a friend. Earn a free month.
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.65, maxWidth: "29rem" }}>
                Members earn{" "}
                <Box component="span" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                  one free month
                </Box>{" "}
                for every friend they refer — useful while each region builds queue density. The more players in your region, the better the matches for everyone.
              </Typography>
            </Box>
            <Box component={Link} to={{ pathname: "/" }} sx={{ ...btnPrimarySx, whiteSpace: "nowrap" }}>
              Start referring
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
              Start free.
              <br />
              Upgrade
              <br />
              when it fits.
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.text.secondary, maxWidth: 400, lineHeight: 1.7 }}>
              Create your account, get verified, and join the founding community. Pick a paid tier later if you want more deliberate queue control.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join for free
            </Box>
            <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
              How it works →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
